import React, { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { blip, speak } from "@/lib/audio";
import { Timer, RotateCcw, X, ShieldAlert } from "lucide-react";

// Officer / firefighter safety (welfare-check) timer for police & fire incidents.
export default function SafetyTimer({ incident, onChange }) {
  const [now, setNow] = useState(Date.now());
  const beeped = useRef(false);
  useEffect(() => { const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, []);

  if (!["police", "fire"].includes(incident.discipline)) return <span className="text-[#4b5563]">—</span>;
  const st = incident.safety_timer;
  const stop = (e) => e?.stopPropagation();
  const start = async (sec, e) => { stop(e); try { await client.post(`/incidents/${incident.id}/safety-timer`, { interval_sec: sec }); toast.success(`Safety timer set — ${Math.round(sec / 60)} min`); onChange?.(); } catch (er) { toast.error(er.response?.data?.detail || "Failed"); } };
  const clear = async (e) => { stop(e); try { await client.delete(`/incidents/${incident.id}/safety-timer`); onChange?.(); } catch { toast.error("Failed"); } };

  if (!st?.enabled) {
    const def = incident.discipline === "fire" ? 1200 : 600;
    return (
      <div className="flex items-center gap-1" onClick={stop}>
        <button data-testid={`safety-start-${incident.cc_number}`} onClick={(e) => start(def, e)}
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-head border border-[#2C2D35] text-[#9CA3AF] hover:text-white hover:border-[#F59E0B] transition-colors">
          <Timer size={11} /> Set {def / 60}m
        </button>
      </div>
    );
  }

  const rem = Math.floor((new Date(st.expires_at).getTime() - now) / 1000);
  const due = rem <= 0;
  if (due && !beeped.current) { try { blip(); speak(`Status check due. ${incident.cc_number}. ${(incident.assigned_units || []).join(", ")}.`); } catch { /* noop */ } beeped.current = true; }
  if (!due) beeped.current = false;
  const mm = String(Math.floor(Math.abs(rem) / 60)).padStart(2, "0");
  const ss = String(Math.abs(rem) % 60).padStart(2, "0");

  return (
    <div className="flex items-center gap-1.5" onClick={stop} data-testid={`safety-timer-${incident.cc_number}`}>
      {due ? (
        <span className="flex items-center gap-1 font-mono text-[11px] font-bold text-[#EF4444] animate-pulse" data-testid={`safety-due-${incident.cc_number}`}>
          <ShieldAlert size={12} /> CHECK +{mm}:{ss}
        </span>
      ) : (
        <span className="font-mono text-[11px] font-bold text-[#F59E0B] tabular-nums">{mm}:{ss}</span>
      )}
      <button data-testid={`safety-reset-${incident.cc_number}`} title="Status check OK — reset timer" onClick={(e) => start(st.interval_sec, e)}
        className="text-[#22C55E] hover:text-white"><RotateCcw size={13} /></button>
      <button data-testid={`safety-clear-${incident.cc_number}`} title="Clear timer" onClick={clear}
        className="text-[#6B7280] hover:text-[#EF4444]"><X size={13} /></button>
    </div>
  );
}
